/** Instance-owned presentation extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamPresentation(runtime) {
  const text = (node, value) => {
    const next = String(value);
    if (node.textContent !== next) node.textContent = next;
  };
  runtime.roundRect = function (x, y, w, h, r) {
    runtime.roundRectCtx(runtime.ctx, x, y, w, h, r);
  };
  runtime.roundRectCtx = function (target, x, y, w, h, r) {
    const radius = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
    target.beginPath();
    if (typeof target.roundRect === 'function') {
      target.roundRect(x, y, w, h, radius);
      return;
    }
    target.moveTo(x + radius, y);
    target.lineTo(x + w - radius, y);
    target.quadraticCurveTo(x + w, y, x + w, y + radius);
    target.lineTo(x + w, y + h - radius);
    target.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    target.lineTo(x + radius, y + h);
    target.quadraticCurveTo(x, y + h, x, y + h - radius);
    target.lineTo(x, y + radius);
    target.quadraticCurveTo(x, y, x + radius, y);
  };
  runtime.syncHud = function (options = {}) {
    const now = runtime.now();
    const throttleMs = runtime.isMobilePressureMode()
      ? runtime.game.energyMode === 'pressureGuard'
        ? runtime.HUD_SLOW_SYNC_MS
        : runtime.HUD_FAST_SYNC_MS
      : 0;
    if (!options.force && throttleMs > 0 && now - runtime.game.hudLastSyncAt < throttleMs) return;
    runtime.game.hudLastSyncAt = now;
    const modeConfig = runtime.currentModeConfig();
    text(runtime.ui.level, runtime.game.level);
    text(runtime.ui.balls, runtime.game.ballCount);
    text(runtime.ui.coins, runtime.formatCashNumber(runtime.game.coins));
    text(runtime.ui.score, runtime.formatPinballNumber(runtime.game.score, 12));
    if (runtime.ui.autoUpgrade) runtime.ui.autoUpgrade.checked = runtime.game.autoUpgrade;
    if (runtime.ui.launchState) {
      const waitLeft = runtime.getLaunchCountdownSeconds();
      const userBalls = runtime.game.activeUserBalls;
      if (modeConfig.durationSeconds)
        runtime.ui.launchState.textContent =
          runtime.game.speedQueuedRows > 0
            ? `Speed catch-up: ${runtime.game.speedQueuedRows} queued; time ${runtime.formatClock(runtime.game.modeTimerRemaining)}`
            : `Speed time: ${runtime.formatClock(runtime.game.modeTimerRemaining)}; row ${Math.max(0, runtime.SPEED_TIMED_PRESSURE_SECONDS - runtime.game.speedTimedPressureElapsed).toFixed(1)}s; ${Math.max(0, runtime.game.speedNextAdvanceBlocks - runtime.game.destroyedBlocksSinceAdvance)} blocks`;
      else if (modeConfig.timedRowAdvanceSeconds)
        runtime.ui.launchState.textContent = `Next row: ${Math.max(0, runtime.game.timedCurrentRowAdvanceSeconds - runtime.game.timedRowAdvanceElapsed).toFixed(1)}s; user balls ${userBalls}/${runtime.USER_LAUNCHED_BALL_CAP}; fuel ${runtime.game.neutronFuel}/${runtime.game.neutronFuelCost}`;
      else
        runtime.ui.launchState.textContent = runtime.game.volleyActive
          ? `Volley live: ${runtime.game.balls.length + runtime.game.launchQueue} in play; relaunch in ${waitLeft.toFixed(1)}s`
          : runtime.game.status === 'gameover'
            ? 'Run ended'
            : 'Ready to launch';
    }
    if (runtime.ui.angle) runtime.ui.angle.textContent = `${runtime.game.angle}°`;
    for (const [key, cost] of Object.entries(runtime.game.costs)) {
      if (!runtime.ui.costs[key]) continue;
      runtime.ui.costs[key].textContent =
        key === 'launchTime'
          ? runtime.formatLaunchWaitUpgradeValue()
          : runtime.isUpgradeCapped(key)
            ? 'MAX'
            : runtime.formatGroupedNumber(cost);
    }
    runtime.ui.upgradeButtons.forEach((button) => {
      button.disabled =
        runtime.game.coins < runtime.game.costs[button.dataset.upgrade] ||
        runtime.game.status === 'gameover' ||
        runtime.isUpgradeCapped(button.dataset.upgrade);
    });
    if (runtime.ui.launch) {
      const rapidLaunchUnavailable =
        runtime.isRapidLaunchMode() &&
        (runtime.game.launchQueue > 0 || !runtime.canAcceptRapidUserLaunch());
      runtime.ui.launch.disabled =
        rapidLaunchUnavailable ||
        (!runtime.isRapidLaunchMode() &&
          runtime.game.volleyActive &&
          !runtime.canRelaunchDuringVolley()) ||
        runtime.game.status === 'gameover';
    }
    runtime.syncModeControls();
  };
  runtime.formatClock = function (seconds) {
    const whole = Math.max(0, Math.ceil(seconds));
    const minutes = Math.floor(whole / 60);
    return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
  };
  runtime.formatPinballNumber = function (value, digits) {
    const whole = Math.max(0, Math.floor(value));
    if (whole >= 10 ** digits) return runtime.formatBigNumber(whole);
    return runtime.formatGroupedNumber(whole);
  };
  runtime.formatCashNumber = function (value) {
    return `$${runtime.formatGroupedNumber(value)}`;
  };
  runtime.formatGroupedNumber = function (value) {
    return Math.max(0, Math.floor(Number(value) || 0)).toLocaleString('en-US');
  };
  runtime.formatBigNumber = function (value) {
    const abs = Math.abs(value);
    if (abs < 1000) return String(Math.floor(value));
    const units = [
      [1e15, 'Q'],
      [1e12, 'T'],
      [1e9, 'B'],
      [1e6, 'M'],
      [1e3, 'K'],
    ];
    for (const [size, suffix] of units) {
      if (abs >= size) {
        const scaled = value / size;
        const digits = Math.abs(scaled) >= 100 ? 0 : Math.abs(scaled) >= 10 ? 1 : 2;
        return `${scaled.toFixed(digits).replace(/\\.0+$|(?<=\\.\\d)0+$/g, '')}${suffix}`;
      }
    }
    return String(Math.floor(value));
  };
  runtime.canvasPointFromEvent = function (event) {
    const rect = runtime.canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * runtime.W,
      y: ((event.clientY - rect.top) / rect.height) * runtime.H,
    };
  };
  runtime.setAngleFromPoint = function (clientX, clientY) {
    const rect = runtime.canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * runtime.W;
    const y = ((clientY - rect.top) / rect.height) * runtime.H;
    const deg = Math.atan2(runtime.launcher.y - y, x - runtime.launcher.x) * (180 / Math.PI);
    runtime.game.angle = Math.round(runtime.clamp(deg, 16, 164));
    if (runtime.ui.slider) runtime.ui.slider.value = runtime.game.angle;
    runtime.syncHud();
  };
  runtime.showOverlay = function (state, title, copy) {
    runtime.ui.overlay.classList.add('is-visible');
    runtime.game.scoreboardMode = state === 'Game Over' ? 'gameover' : 'default';
    runtime.ui.primary.textContent = state === 'Game Over' ? 'Restart Run' : 'Start Run';
    runtime.ui.overlayTitle.textContent = title;
    runtime.ui.overlayCopy.textContent = copy;
    runtime.syncModeControls();
    if (state !== 'Game Over') runtime.hideScoreboard();
    else runtime.renderScoreboard();
  };
  runtime.hideOverlay = function () {
    runtime.ui.overlay.classList.remove('is-visible');
  };
  runtime.handlePrimaryAction = function (event) {
    if (
      runtime.game.scoreboardMode === 'view' &&
      runtime.ui.overlay.classList.contains('is-visible')
    ) {
      event?.preventDefault?.();
      if (runtime.game.status === 'ready') {
        const modeConfig = runtime.currentModeConfig();
        runtime.showOverlay('Ready', modeConfig.overlayTitle, modeConfig.overlayCopy);
      } else if (runtime.game.status === 'gameover') {
        runtime.prepareGameOverScoreboard();
        runtime.showOverlay(
          'Game Over',
          `${runtime.modeLabel()} run complete`,
          'Save your score or restart for another run.',
        );
      } else runtime.hideOverlay();
      return;
    }
    if (runtime.game.status === 'gameover') runtime.resetGame({ preserveMode: true });
    runtime.forceLaunch(event);
  };
}
